import { useState } from "react";
import {
    Modal, ModalBody, ModalHeader, ModalFooter,
    Button, Form, FormGroup, Label, Input, Table,
} from "reactstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { issueRefund } from "../../api/api";
import Loader from "../../components/Loader";

function IssueRefundModal({ isOpen, toggle, order }) {
    const [refundType, setRefundType] = useState("FULL");
    const [refundReason, setRefundReason] = useState("");
    const [selectedItems, setSelectedItems] = useState({});
    const [customAmount, setCustomAmount] = useState("");
    const queryClient = useQueryClient();

    const isCash = order?.payment_method === "CASH";
    const orderTotal = parseFloat(order?.total_amount || 0);

    const resetForm = () => {
        setRefundType("FULL");
        setRefundReason("");
        setSelectedItems({});
        setCustomAmount("");
    };

    const handleToggle = () => {
        resetForm();
        toggle();
    };

    const handleRefundTypeChange = (type) => {
        setRefundType(type);
        setSelectedItems({});
        setCustomAmount("");
    };

    const handleItemToggle = (lineItemId) => {
        setSelectedItems((prev) => {
            const updated = { ...prev };
            if (updated[lineItemId]) {
                delete updated[lineItemId];
            } else {
                const item = order.order_items.find((i) => i.id === lineItemId);
                updated[lineItemId] = {
                    order_line_item: lineItemId,
                    quantity: item?.quantity || 1,
                    maxQuantity: item?.quantity || 1,
                };
            }
            return updated;
        });
    };

    const handleQuantityChange = (lineItemId, value) => {
        const qty = parseInt(value, 10);
        if (isNaN(qty) || qty < 1) return;
        setSelectedItems((prev) => ({
            ...prev,
            [lineItemId]: {
                ...prev[lineItemId],
                quantity: Math.min(qty, prev[lineItemId].maxQuantity),
            },
        }));
    };

    const estimatedPartialTotal = Object.entries(selectedItems).reduce(
        (total, [itemId, sel]) => {
            const item = order.order_items.find((i) => i.id === itemId);
            if (!item) return total;
            const perUnit = parseFloat(item.total_amount) / item.quantity;
            return total + perUnit * sel.quantity;
        },
        0
    );

    const customAmountFloat = parseFloat(customAmount);
    const customAmountInvalid =
        customAmount !== "" &&
        (isNaN(customAmountFloat) || customAmountFloat <= 0 || customAmountFloat > orderTotal);

    const issueRefundMutation = useMutation({
        mutationFn: issueRefund,
        onSuccess: () => {
            queryClient.invalidateQueries(["orders"]);
            toast.success("Refund issued successfully! A credit note has been created.");
            handleToggle();
        },
        onError: (err) => {
            const data = err?.response?.data;
            if (data && typeof data === "object") {
                const firstError =
                    data.detail ||
                    data.non_field_errors?.[0] ||
                    Object.values(data)[0];
                toast.error(
                    `Refund failed: ${Array.isArray(firstError) ? firstError[0] : firstError}`
                );
            } else {
                toast.error("An error occurred while issuing the refund.");
            }
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!refundReason.trim()) {
            toast.error("Please provide a reason for the refund.");
            return;
        }

        const payload = {
            order: order.id,
            refund_type: refundType,
            refund_reason: refundReason,
        };

        if (refundType === "PARTIAL") {
            payload.amount = estimatedPartialTotal.toFixed(2);
            payload.line_items = Object.values(selectedItems).map((item) => ({
                order_line_item: item.order_line_item,
                quantity: item.quantity,
            }));
        } else if (refundType === "PARTIAL_CUSTOM") {
            payload.amount = customAmountFloat.toFixed(2);
        }

        issueRefundMutation.mutate(payload);
    };

    const selectedCount = Object.keys(selectedItems).length;

    const isSubmitDisabled =
        issueRefundMutation.isLoading ||
        !refundReason.trim() ||
        (refundType === "PARTIAL" && selectedCount === 0) ||
        (refundType === "PARTIAL_CUSTOM" && (!customAmount || customAmountInvalid));

    const submitLabel = () => {
        if (issueRefundMutation.isLoading) return <><Loader size="small" /> Processing...</>;
        if (refundType === "FULL") return "Issue Full Refund";
        if (refundType === "PARTIAL") return "Issue Partial Refund";
        return "Issue Custom Refund";
    };

    return (
        <Modal className="modal-lg" isOpen={isOpen} toggle={handleToggle}>
            <ModalHeader
                toggle={handleToggle}
                close={<button className="close" onClick={handleToggle}>&times;</button>}
            >
                Issue Refund — Order #{order?.order_number}
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {/* Info banner */}
                    <div
                        className="alert mb-3"
                        style={{
                            backgroundColor: "rgba(77,91,237,0.08)",
                            border: "1px solid rgba(77,91,237,0.25)",
                            borderRadius: 6,
                            fontSize: 13,
                            color: "#9097a7",
                        }}
                    >
                        <i className="mdi mdi-information-outline mr-2" style={{ color: "#4d5bed" }} />
                        A <strong style={{ color: "#f1f5f7" }}>credit note</strong> will be automatically
                        created and issued for this refund.
                        {!isCash && (
                            <> The refund will be processed via{" "}
                                <strong style={{ color: "#f1f5f7" }}>Razorpay</strong> and credited to the
                                customer&apos;s original payment method.
                            </>
                        )}
                        <div className="mt-1" style={{ color: "#e2a03f" }}>
                            <i className="mdi mdi-alert-outline mr-1" />
                            Only one refund can be issued per order. Choose the refund type carefully.
                        </div>
                    </div>

                    {/* Refund Type selector */}
                    <FormGroup>
                        <Label>Refund Type</Label>
                        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                            <Button
                                type="button"
                                color={refundType === "FULL" ? "primary" : "secondary"}
                                size="sm"
                                onClick={() => handleRefundTypeChange("FULL")}
                            >
                                Full Refund (₹{order?.total_amount})
                            </Button>
                            <Button
                                type="button"
                                color={refundType === "PARTIAL" ? "primary" : "secondary"}
                                size="sm"
                                onClick={() => handleRefundTypeChange("PARTIAL")}
                            >
                                By Items
                            </Button>
                            <Button
                                type="button"
                                color={refundType === "PARTIAL_CUSTOM" ? "primary" : "secondary"}
                                size="sm"
                                onClick={() => handleRefundTypeChange("PARTIAL_CUSTOM")}
                            >
                                Custom Amount
                            </Button>
                        </div>
                    </FormGroup>

                    {/* By Items: item selector table */}
                    {refundType === "PARTIAL" && (
                        <>
                            <Label>Select Items to Refund</Label>
                            <Table bordered size="sm" responsive className="mb-2">
                                <thead className="bg-light">
                                    <tr>
                                        <th style={{ width: 40 }}></th>
                                        <th>Item Name</th>
                                        <th>Type</th>
                                        <th>Rate</th>
                                        <th>Order Qty</th>
                                        <th>Refund Qty</th>
                                        <th>Tax %</th>
                                        <th>Refund Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.order_items.map((item) => {
                                        const isSelected = !!selectedItems[item.id];
                                        const refundQty = selectedItems[item.id]?.quantity || 0;
                                        const perUnit = parseFloat(item.total_amount) / item.quantity;
                                        const itemTotal = isSelected
                                            ? (perUnit * refundQty).toFixed(2)
                                            : "0.00";

                                        return (
                                            <tr key={item.id} className={isSelected ? "table-active" : ""}>
                                                <td className="text-center align-middle">
                                                    <Input
                                                        type="checkbox"
                                                        style={{ margin: 0, display: 'block' }}
                                                        checked={isSelected}
                                                        onChange={() => handleItemToggle(item.id)}
                                                    />
                                                </td>
                                                <td>{item.name}</td>
                                                <td>{item.type}</td>
                                                <td>₹{item.rate}</td>
                                                <td>{item.quantity}</td>
                                                <td style={{ width: 90 }}>
                                                    {isSelected ? (
                                                        <Input
                                                            type="number"
                                                            min={1}
                                                            max={item.quantity}
                                                            value={selectedItems[item.id]?.quantity}
                                                            onChange={(e) =>
                                                                handleQuantityChange(item.id, e.target.value)
                                                            }
                                                            bsSize="sm"
                                                        />
                                                    ) : "-"}
                                                </td>
                                                <td>{item.tax_percentage}%</td>
                                                <td>{isSelected ? `₹${itemTotal}` : "-"}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                            {selectedCount > 0 && (
                                <div className="text-end mb-3" style={{ fontSize: 13 }}>
                                    <strong>Estimated Refund Amount: </strong>
                                    <span style={{ color: "#1abc9c", fontWeight: 700 }}>
                                        ₹{estimatedPartialTotal.toFixed(2)}
                                    </span>
                                </div>
                            )}
                        </>
                    )}

                    {/* Custom Amount input */}
                    {refundType === "PARTIAL_CUSTOM" && (
                        <FormGroup>
                            <Label for="custom-amount">
                                Refund Amount (₹) <span className="text-danger">*</span>
                            </Label>
                            <Input
                                type="number"
                                id="custom-amount"
                                min="0.01"
                                step="0.01"
                                value={customAmount}
                                onChange={(e) => setCustomAmount(e.target.value)}
                                placeholder={`Enter amount (max ₹${order?.total_amount})`}
                                invalid={customAmountInvalid}
                            />
                            {customAmountInvalid && (
                                <div className="invalid-feedback" style={{ display: "block" }}>
                                    {customAmountFloat > orderTotal
                                        ? `Amount cannot exceed order total of ₹${order?.total_amount}`
                                        : "Enter a valid amount greater than ₹0"}
                                </div>
                            )}
                            <small className="text-muted">
                                A goodwill adjustment credit note will be created with no GST breakdown.
                                If you need to reverse GST, use <strong>By Items</strong> instead.
                            </small>
                        </FormGroup>
                    )}

                    {/* Reason */}
                    <FormGroup>
                        <Label for="refund-reason">
                            Refund Reason <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="textarea"
                            id="refund-reason"
                            rows={2}
                            value={refundReason}
                            onChange={(e) => setRefundReason(e.target.value)}
                            placeholder="e.g. Customer requested refund, Defective item, Goodwill adjustment"
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" type="button" onClick={handleToggle}>
                        Cancel
                    </Button>
                    <Button color="danger" type="submit" disabled={isSubmitDisabled}>
                        {submitLabel()}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
}

export default IssueRefundModal;
